import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import type { Product } from "@/types/products";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { checkIsNewProduct, stripHTML } from "@/utils/utils";
import { getIsPurchasable } from "@/helpers/helpers";
import { CACHE_CONFIG, buildProductDetailCacheKey, getValidCacheData } from "@/utils/cacheManager";
import type { CacheItem } from "@/store/cacheReducer";

interface FetchProductViewModel {
  product: Product | null;
  isPurchasable: boolean;
  isProductLoading: boolean;
  isNewProduct: boolean;
}

// Helper
const formatProductData = (product: Product): Product => ({
  ...product,
  name: stripHTML(product.name),
  short_description: stripHTML(product.short_description),
});

const useFetchProduct = (): FetchProductViewModel => {
  const { productSlug } = useParams();
  const appDispatch = useAppDispatch();

  const cacheKey = useMemo(() => buildProductDetailCacheKey(productSlug ?? ""), [productSlug]);
  const cacheState = useSelector((store: RootState) => store.cache);
  const cachedProduct = useMemo(
    () => getValidCacheData<Product>(cacheKey, cacheState),
    [cacheKey, cacheState],
  );

  const product = useMemo(
    () => (cachedProduct ? formatProductData(cachedProduct) : null),
    [cachedProduct],
  );

  const [isLoading, setIsLoading] = useState(false);

  const isPurchasable = useMemo(() => getIsPurchasable(product), [product]);
  const isNewProduct = useMemo(() => checkIsNewProduct(product?.categories ?? []), [product]);

  const fetchProduct = async (signal?: AbortSignal) => {
    if (!productSlug) return;
    setIsLoading(true);

    try {
      const { data } = await wooStoreApiService.product.getItem(productSlug, signal);

      const cacheItem: CacheItem = {
        data,
        expiry: CACHE_CONFIG.productDetail.expiry,
        lastFetched: Date.now(),
      };

      appDispatch.setCache(cacheKey, cacheItem);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "取得商品資料失敗，請稍後再試");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    if (!productSlug || cachedProduct) return;
    fetchProduct(controller.signal);

    return () => {
      controller.abort();
    };
  }, [productSlug, cachedProduct]);

  return { product, isPurchasable, isProductLoading: isLoading, isNewProduct };
};

export default useFetchProduct;
